import React, { useState, useMemo } from 'react';
import { 
  DataGrid, 
  GridColDef, 
  GridRowsProp, 
  GridPaginationModel,
  GridInitialState,
  GridRowSelectionModel
} from '@mui/x-data-grid';
import { Box, Paper } from '@mui/material';
import { deDE, enUS } from '@mui/x-data-grid/locales';
import { useI18n } from '@/hooks/useI18n';

interface ReusableDataGridProps {
  rows: GridRowsProp;
  columns: GridColDef[];
  loading?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  disableColumnFilter?: boolean;
  disableColumnSelector?: boolean;
  disableDensitySelector?: boolean;
  disableExport?: boolean;
  showToolbar?: boolean;
  hideFooter?: boolean;
  autoHeight?: boolean;
  rowHeight?: number;
  initialState?: GridInitialState;
  getRowId?: (row: GridRowsProp[number]) => string;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  paginationModel?: GridPaginationModel;
  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (selectionModel: GridRowSelectionModel) => void;
}

const ReusableDataGrid: React.FC<ReusableDataGridProps> = ({
  rows,
  columns,
  loading = false,
  pageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  disableColumnFilter = false,
  disableColumnSelector = false,
  disableDensitySelector = false,
  disableExport = false,
  showToolbar = true,
  hideFooter = false,
  rowHeight,
  initialState,
  getRowId,
  onPaginationModelChange,
  paginationModel,
  checkboxSelection = false,
  rowSelectionModel,
  onRowSelectionModelChange,
}) => {
  const { i18n } = useI18n();
  
  // Get the appropriate locale for DataGrid based on current language
  const getDataGridLocale = () => {
    switch (i18n.language) {
      case 'de':
        return deDE.components.MuiDataGrid.defaultProps.localeText;
      case 'en':
        return enUS.components.MuiDataGrid.defaultProps.localeText;
      default:
        return deDE.components.MuiDataGrid.defaultProps.localeText; // Default to German
    }
  };

  // Internal state for pagination when no external state is provided
  const [internalPaginationModel, setInternalPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: pageSize,
  });

  // Use external paginationModel if provided, otherwise use internal state
  const currentPaginationModel = paginationModel || internalPaginationModel;

  // Handle pagination model changes
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (onPaginationModelChange) {
      // If external handler is provided, use it
      onPaginationModelChange(model);
    } else {
      // Otherwise, update internal state
      setInternalPaginationModel(model);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={1} sx={{ borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          checkboxSelection={checkboxSelection}
          disableRowSelectionOnClick
          getRowId={getRowId}
          paginationModel={currentPaginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          hideFooter={hideFooter}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          showToolbar={showToolbar}
          disableColumnFilter={disableColumnFilter}
          disableColumnSelector={disableColumnSelector}
          disableDensitySelector={disableDensitySelector}
          {...(rowHeight && { rowHeight })}
          {...(initialState && { initialState })}
          localeText={getDataGridLocale()}
          slotProps={{
            toolbar: {
              csvOptions: {
                delimiter: ';',
                utf8WithBom: true,
                fileName: 'Reiseleiter Pro Export',
              },
            },
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '8px 16px',
              gap: 1,
              backgroundColor: 'background.default',
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
              fontSize: '0.875rem',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default ReusableDataGrid;
